import {Dispatch, FC, SetStateAction, useEffect, useId, useState} from 'react'
import {CustomModal} from '_metronic/partials/modals/CustomModal'
import * as Yup from 'yup'
import {useFormik} from 'formik'
import Input from '../Input'
import db from 'services/firebase'
import {addDoc, collection, serverTimestamp, updateDoc} from '@firebase/firestore'
import {restrictedDomains} from 'app/modules/auth/core/restrictedDomain'
import {toast} from 'react-toastify'
import MultiSelect from '../MultiSelect'
import {decryptId} from '../../utils/helpers'

type Props = {
  isModalOpen: boolean
  hideModal: () => void
  encryptedTenantId: string
  resources: {id: string; title: string; documentURL: string}[]
  requestedResource?: {id: string}
  setRequestedResourcesIdArr: Dispatch<SetStateAction<string[]>>
  companyName: string
  masterServiceAgreementLink: string
}

const initialValues = {
  firstname: '',
  lastname: '',
  email: '',
  company: '',
  resources: [] as any[],
  acceptMasterServiceAgreement: false,
}

const validationSchema = Yup.object().shape({
  firstname: Yup.string()
    .min(3, 'Minimum 3 characters')
    .max(50, 'Maximum 50 characters')
    .required('First name is required'),
  lastname: Yup.string()
    .min(3, 'Minimum 3 characters')
    .max(50, 'Maximum 50 characters')
    .required('Last name is required'),
  email: Yup.string()
    .min(3, 'Minimum 8 characters')
    .max(50, 'Maximum 50 characters')
    .required('Email is required')
    .matches(/^[^\s@]+@[^\s@]+\.[^\s@]+$/, 'Please enter a valid email address')
    .test('is-work-email', 'Please use your company work email', (value) => {
      const domain = value.split('@')[1]?.toLowerCase()
      if (!domain) return false
      return !restrictedDomains.includes(domain)
    }),
  company: Yup.string()
    .min(3, 'Minimum 3 characters')
    .max(50, 'Maximum 50 characters')
    .required('Company name is required'),
  resources: Yup.array().min(1, 'Please select at least one resource'),
  acceptMasterServiceAgreement: Yup.bool().oneOf(
    [true],
    'You must accept Master Service Agreement'
  ),
})

const RequestAccessModal: FC<Props> = ({
  isModalOpen,
  hideModal,
  encryptedTenantId,
  resources,
  requestedResource,
  setRequestedResourcesIdArr,
  companyName,
  masterServiceAgreementLink,
}) => {
  const [tenantId, setTenantId] = useState('')
  const [loading, setLoading] = useState(false)
  const [resourceSelectError, setResourceError] = useState('')

  useEffect(() => setTenantId(decryptId(encryptedTenantId)), [encryptedTenantId])

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: async (values) => {
      setLoading(true)
      try {
        const requestAccessRef = collection(db, 'tenants', tenantId, 'requests_access')

        const newDocRef = await addDoc(requestAccessRef, {
          ...values,
          requestedDate: serverTimestamp(),
          status: 'Needs Decision',
        })

        await updateDoc(newDocRef, {
          id: newDocRef.id,
        })

        setRequestedResourcesIdArr((prev) => [
          ...prev,
          ...values.resources.map((resource) => resource.id),
        ])

        toast.success('Request access for resource has been sent successfully', {
          position: toast.POSITION.TOP_CENTER,
          className: 'bg-white',
          style: {
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
            border: '1px solid #e0e0e0',
            color: 'black',
          },
          progressStyle: {
            background: 'black',
          },
          icon: () => (
            <svg
              width='26'
              height='26'
              viewBox='0 0 24 24'
              fill='none'
              xmlns='http://www.w3.org/2000/svg'
              style={{color: '#0E9F6E'}}
            >
              <path
                d='M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM10 17L5 12L6.41 10.59L10 14.17L17.59 6.58L19 8L10 17Z'
                fill='black'
              />
            </svg>
          ),
          autoClose: 3000,
        })
      } catch (error) {
        console.error('Error during request access for trust center: ', error)
      } finally {
        setLoading(false)
        hideModal()
      }
    },
  })

  const okBtnClickHander = () => {
    if (formik.values.resources.length === 0) {
      setResourceError('Please select at least one resource')
    }
    formik.submitForm()
  }

  const toggleResources = (resourceId: string) => {
    const currentResources = formik.values.resources || []
    const isFound = currentResources.some((curr) => curr.id === resourceId)

    if (isFound) {
      formik.setFieldValue(
        'resources',
        currentResources.filter((curr) => curr.id !== resourceId)
      )

      if (currentResources.length === 1) {
        setResourceError('Please select at least one resource')
      }
    } else {
      const matchedResource = resources.filter((resource) => resource.id === resourceId)[0]
      formik.setFieldValue('resources', [...currentResources, matchedResource])
      setResourceError('')
    }
  }

  const removeResource = (resourceId: string) => {
    formik.setFieldValue('resources', [
      ...formik.values.resources.filter((curr) => curr.id !== resourceId),
    ])

    if (formik.values.resources.length === 1) {
      setResourceError('Please select at least one resource')
    }
  }

  useEffect(() => {
    if (requestedResource?.id) {
      const resource = resources?.find((resource) => resource.id === requestedResource.id)
      if (resource) {
        formik.setFieldValue('resources', [
          {id: resource?.id, title: resource?.title, documentURL: resource?.documentURL},
        ])
      }
    }
  }, [requestedResource])

  const id = useId()

  return (
    <CustomModal
      isOpen={isModalOpen}
      onClose={hideModal}
      title='Request Access'
      isCloseIconShown={true}
      okBtnText='Request Access'
      okBtnClickHandler={okBtnClickHander}
      okBtnType='black'
      cancelBtnText={'Cancel'}
      isLoading={loading}
      isLoadingBtnText='Submitting...'
    >
      <div
        className='d-flex flex-column align-items-center justify-content-center'
        style={{gap: '20px', marginBottom: '24px'}}
      >
        <div
          className='w-100 d-flex justify-content-between align-items-start'
          style={{gap: '12px'}}
        >
          <Input
            type='text'
            label='First Name'
            placeholder='John'
            autoComplete='off'
            error={
              formik.touched.firstname && formik.errors.firstname ? formik.errors.firstname : ''
            }
            {...formik.getFieldProps('firstname')}
          />
          <Input
            type='text'
            label='Last Name'
            placeholder='Doe'
            autoComplete='off'
            error={formik.touched.lastname && formik.errors.lastname ? formik.errors.lastname : ''}
            {...formik.getFieldProps('lastname')}
          />
        </div>

        <Input
          type='email'
          label='Work Email'
          placeholder='john@company.org'
          autoComplete='off'
          error={formik.touched.email && formik.errors.email ? formik.errors.email : ''}
          {...formik.getFieldProps('email')}
        />

        <Input
          type='text'
          label='Company'
          placeholder='Your company name'
          autoComplete='off'
          error={formik.touched.company && formik.errors.company ? formik.errors.company : ''}
          {...formik.getFieldProps('company')}
        />
      </div>

      <div style={{marginBottom: '24px'}}>
        <MultiSelect
          label='Select Resources'
          placeholder="Select Resources"
          options={resources.map((resource) => ({id: resource.id, title: resource.title}))}
          selectedList={formik.values.resources}
          toggleList={toggleResources}
          removeItem={removeResource}
          error={resourceSelectError}
          checkedBoxBackgroundColor='black'
        />
      </div>

      <div className='d-flex flex-column' style={{gap: '8px'}}>
        <label className='form-check form-check-inline' htmlFor={id}>
          <input
            className='form-check-input cursor-pointer form-check-black'
            type='checkbox'
            id={id}
            {...formik.getFieldProps('acceptMasterServiceAgreement')}
          />
          <span className='text-custom-primary-gray' style={{fontSize: '13px'}}>
            I have read and accepted
            <a href={masterServiceAgreementLink} target='_blank' className='ms-2'>
              <span className='text-custom-primary-gray fw-bold text-decoration-underline'>
                {companyName ? `${companyName}'s ` : ''}Master Service Agreement
              </span>
            </a>
            .
          </span>
        </label>

        {formik.touched.acceptMasterServiceAgreement && formik.errors.acceptMasterServiceAgreement && (
          <div className='fv-plugins-message-container'>
            <div className='fv-help-block'>
              <span role='alert'>{formik.errors.acceptMasterServiceAgreement}</span>
            </div>
          </div>
        )}
      </div>
    </CustomModal>
  )
}

export default RequestAccessModal
